import { computed, observable, runInAction } from 'mobx';

import {
  makePaginated,
  Paginated
} from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import {
  AppUser,
  Competency,
  LearningProvider,
  Skill,
  TrainingOffering
} from '../../adl-gen/totalinteraction/futurefit/db';
import {
  Entry,
  RepositoryEntries,
  RepositoryEntrySet,
  TrainingOfferingData
} from '../../adl-gen/totalinteraction/futurefit/frontend';
import {
  makeRepositoryPutReq,
  makeRepositoryReq,
  QueryTrainingOfferingDataReq,
  RepositoryPutEntry,
  RepositoryPutReq,
  RepositoryReq
} from '../../adl-gen/totalinteraction/futurefit/requests';
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { TrainingOfferingListEntry } from '../ui/components/lists/training-offering-lists/training-offering-list-view';

import { DataStore, TransportLayer } from './datastore';
import { SkillTrainingOfferingChange, SkillTrainingOfferingHelper } from './skill-training-offering-helper';

export interface TrainingOfferingCompetencySkillsMap {
  competencies: Map<DbKey<Competency>, Competency>;
  skillsByCompetency: Map<DbKey<Competency>, WithDbId<Skill>[]>;
  trainingOfferingsBySkill: Map<DbKey<Skill>, DbKey<TrainingOffering>[]>;
}

/// For when TROs are assigned to Competencies instead
export interface TrainingOfferingCompetencyMap {
  competencies: Map<DbKey<Competency>, Competency>;
  trainingOfferingsByCompetency: Map<DbKey<Competency>, DbKey<TrainingOffering>[]>;
}

export interface TrainingOfferingListAndData {
  list: Paginated<TrainingOfferingListEntry>;
  data: TrainingOfferingData;
}

class TrainingOfferingApi implements TransportLayer<TrainingOffering> {
  kind: string = 'TrainingOffering';
  constructor(protected readonly service: Service) {}
  create = async (item: TrainingOffering): Promise<DbKey<TrainingOffering>> =>
    this.service.createTrainingOffering(item);
  update = async (itemWithId: WithDbId<TrainingOffering>): Promise<void> =>
    this.service.updateTrainingOffering(itemWithId);
  doDelete = async (id: DbKey<TrainingOffering>): Promise<void> => this.service.deleteTrainingOffering(id);
  query = async (q: TableQuery): Promise<Paginated<WithDbId<TrainingOffering>>> =>
    this.service.queryTrainingOfferings(q);

  queryLearningProviders = async (q: TableQuery): Promise<Paginated<WithDbId<LearningProvider>>> =>
    this.service.queryLearningProviders(q);

  uploadRepositoryEntry = async (req: RepositoryPutReq): Promise<Entry> => this.service.uploadRepositoryEntry(req);
  listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> =>
    this.service.listRepositoryEntries(req);

  queryTrainingOfferingData = async (req: QueryTrainingOfferingDataReq): Promise<TrainingOfferingData> =>
    this.service.queryTrainingOfferingData(req);
  launchTrainingOfferingUrl = (offeringId: DbKey<TrainingOffering>, userId: DbKey<AppUser>) =>
    this.service.launchTrainingOfferingUrl(offeringId, userId);
}

export class TrainingOfferingStore extends DataStore<TrainingOffering> {
  @observable list: Promise<Paginated<TrainingOfferingListEntry>>;

  @observable selectedEntry: TrainingOfferingListEntry|undefined;

  @observable trainingOfferingData: Promise<TrainingOfferingData>;
  @observable.ref repository: Promise<RepositoryEntrySet>;

  skillTrainingOfferingHelper?: SkillTrainingOfferingHelper;

  static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    id?: DbKey<TrainingOffering>,
  ): TrainingOfferingStore => {
    const store = new TrainingOfferingStore(new TrainingOfferingApi(service), identity, id, service);
    return store;
  };

  constructor(
    transport: TransportLayer<TrainingOffering>,
    identity: LoggedIn | LoggedInSuper,
    id?: DbKey<TrainingOffering>,
    service?: Service,
  ) {
    super(transport, identity, id);
    this.repository = this.initRepository();
    this.trainingOfferingData = this.initData();
    if (service) this.skillTrainingOfferingHelper = SkillTrainingOfferingHelper.fromService(
      service,
      identity
    );

    this.list = this.initList()
      .then(list => runInAction(() => {
        if (id) {
          this.selectedEntry = list.items.find(item => item.id === id);
        }
        return list;
      }));
  }

  private readonly initData = async (): Promise<TrainingOfferingData> => {
    return this.loadTrainingOfferingData();
  };

  private readonly initList = (): Promise<Paginated<TrainingOfferingListEntry>> => {
    return this.loadList();
  };

  private readonly loadList = async (): Promise<Paginated<TrainingOfferingListEntry>> => {
    if (this.authorized) {
      return this.queryList(this.queryAll);
    } else {
      return Promise.reject('Not authorized');
    }
  };


  private readonly listLearningProviders = async (q: TableQuery): Promise<Paginated<WithDbId<LearningProvider>>> => {
    if (this.isSuperUser) {
      return (this.transport as TrainingOfferingApi).queryLearningProviders(q);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  /// S3 Actions
  private readonly initRepository = async (): Promise<RepositoryEntrySet> => {
    const repository: RepositoryEntrySet = {};
    if (this.authorized) {
      return this.loadRepositories(repository);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadRepositoryEntries = async (path: string): Promise<RepositoryEntrySet> => {
    return {
      [path]: await this.listRepositoryEntries(
        makeRepositoryReq({
          appClientId: this.identity.appClient.id,
          path
        })
      )
    };
  };

  private readonly loadRepositories = async (repository: RepositoryEntrySet): Promise<RepositoryEntrySet> => {
    try {
      // Default: Pull out pretty much everything from the database
      const learningProviders = await this.listLearningProviders(this.queryAll);

      const contentPaths = learningProviders.items.map(item => 'data-images/recommendations/' + item.id + '/content/');
      const tilePaths = learningProviders.items.map(item => 'data-images/recommendations/' + item.id + '/tiles/');

      let repos = {};
      repos = await Promise.all(
        [...contentPaths, ...tilePaths].map(async path => {
          const repo = await this.loadRepositoryEntries(path);
          return repo;
        })
      );

      return { ...repository, ...repos };
    } catch (e) {
      return Promise.reject(`Unable to load default repositories: ${(e as Error).message}`);
    }
  };

  private readonly listRepositoryEntries = async (req: RepositoryReq): Promise<RepositoryEntries> => {
    if (this.isSuperUser) {
      return (this.transport as TrainingOfferingApi).listRepositoryEntries(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  private readonly loadTrainingOfferingData = async (): Promise<TrainingOfferingData> => {
    if (this.authorized) {
      // For client admin we set the AppClient here but it's enforced on the server anyway
      // For superuser (later) - set client ID when looking at Role-Range CompetencyTrainingOfferings
      const req: QueryTrainingOfferingDataReq = this.isSuperUser
        ? { kind: 'all', value: {} }
        : { kind: 'appClientId', value: this.identity.appClient.id };
      return (this.transport as TrainingOfferingApi).queryTrainingOfferingData(req);
    } else {
      return Promise.reject('Not authorized');
    }
  };

  uploadRepositoryEntry = async (req: RepositoryPutEntry): Promise<Entry> => {
    if (this.authorized) {
      return (this.transport as TrainingOfferingApi).uploadRepositoryEntry(
        makeRepositoryPutReq({
          appClientId: this.identity.appClient.id,
          entry: req
        })
      );
    } else {
      return Promise.reject('Not authorized');
    }
  };


  @computed
  get loadingData(): MaybeLoading<TrainingOfferingData> {
    if (this.authorized) {
      return fromPromise(this.trainingOfferingData);
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  loadAll = async () => {
    const list = await this.list;
    const data = await this.trainingOfferingData;
    return { list, data };
  }

  @computed
  get loadingAll(): MaybeLoading<TrainingOfferingListAndData> {
    if (this.authorized) {
      return fromPromise(this.loadAll());
    } else {
      return { kind: 'error', error: 'Not authorized' };
    }
  }

  private readonly queryList = async (q: TableQuery): Promise<Paginated<TrainingOfferingListEntry>> => {
    const result = await this.transport.query(q);
    return makePaginated({
      items: result.items.map(item => ({
        id: item.id,
        ...item.value,
        identity: this.isSuperUser ? 'super' : 'client-admin'
      })),
      current_offset: result.current_offset,
      total_size: result.total_size
    });
  };

  // This is a near-clone of the function of the same name in QuizResultsStore
  // ISSUE: the return value is Promise<string>|string, but the promise is not necessarily correctly handled
  public getTrainingOfferingUrl = (offering: TrainingOfferingListEntry) => {
    /// Note that for SuperUser this will cause side-effects (eg enrolments) which will not reference back to any Client
    if (offering.catalogEntryId) {
      return (this.transport as TrainingOfferingApi).launchTrainingOfferingUrl(
        offering.id,
        this.identity.appUserWithDbId.id
      );
    } else {
      return offering.executeUrl;
    }
  };

  public updateSkillTrainingOfferings = async (updates: SkillTrainingOfferingChange[]): Promise<void> => {
    if (this.skillTrainingOfferingHelper)
      await this.skillTrainingOfferingHelper.updateSkillTrainingOfferings(updates);
  };

}




// WEBPACK FOOTER //
// ./src/admin/stores/training-offering-store.ts